import { LitElement, html, css } from 'lit-element';
import { classMap } from 'lit-html/directives/class-map.js';
import { connect } from '../store/connect';
import { flattenObject } from '../util/object-helpers';
import { actions, selectors, constants } from './';
import { selectors as accountSelectors } from '../account';
import { HeaderStyles } from '../styles/header-styles';
import { DETAIL_MODES } from '../form/constants';
import '@trumedianetworks/tmn-core-components/src/tmn-grid';
import '@trumedianetworks/tmn-core-components/src/tmn-button';
import '@trumedianetworks/tmn-core-components/src/tmn-radio';
import '@trumedianetworks/tmn-core-components/src/tmn-tabs';
import '../routing/routed-button';
import '../form/form-element-site';
import '../form/soft-deactivate';
import './site-tokens';
import './site-history';
import '../util/loading-logo';

import { AUTHORIZATIONS } from '../authorizations/authorizations';
import * as routeActions from './route-actions';
const { CAN_EDIT_SITES, CAN_EDIT_ROOT_SITES, CAN_MANAGE_MASTER_TOKENS, CAN_SEE_SITE_HISTORY } =
    AUTHORIZATIONS;
const { STAGING_OVERRIDE_PATH } = constants;
const TABS = {
    PROD: 'PROD',
    STAGING: 'STAGING',
    LEAGUES: 'LEAGUES',
    TOKENS: 'TOKENS',
    HISTORY: 'HISTORY',
};
// TODO - could make selector for "showInformationMessage" or similar to get a message to display when site is either inactive or soft inactive
// todo --
// - possibly a good time to revisit the table filters
// - base class for general edit aspects that we could re-use (detail-base)
// -- controls list could be another component
// -- validation display another component
class SiteDetail extends LitElement {
    constructor() {
        super();
        this.renderTemplateItem = this.renderTemplateItem.bind(this);
        this.detailMode = DETAIL_MODES.VIEW;
        this.selectedTab = TABS.PROD;
    }

    static get styles() {
        return [
            HeaderStyles.styles,
            css`
                :host {
                    max-width: 1200px;
                    background: white;
                    display: flex;
                    flex-flow: column;
                    padding: 8px;
                    margin-top: 2px;
                    min-width: 450px;
                }
                .group {
                    margin: 0 10px 6px 10px;
                    /* max-width: 1024px; */
                }
                .errors {
                    border: 1px solid var(--app-error-color);
                    padding: 8px;
                }
                .errors h2 {
                    color: var(--app-error-color);
                }
                .controls-container {
                    display: flex;
                    position: sticky;
                    top: 20px;
                    z-index: 1;
                }
                .controls-container > * {
                    margin: 0 12px 8px 0;
                }
                .fixed-header {
                    position: sticky;
                    top: 0px;
                    z-index: 3;
                    margin-top: -1px;
                    background-color: var(--app-surface-color);
                }
                .group .group * {
                    flex-grow: 0;
                }
                .section {
                    background: #fafafa;
                    padding: 10px;
                    max-width: 98%;
                }
                .site-season {
                    max-width: 1200px;
                }
                tmn-tab-panel {
                    min-width: 800px;
                }
                loading-logo {
                    position: absolute;
                }
            `,
        ];
    }

    static get properties() {
        return {
            siteTemplate: {
                type: Object,
            },
            validationErrors: {
                type: Object,
            },
            passedValidations: {
                type: Object,
            },
            siteData: {
                type: Object,
            },
            detailMode: {
                type: String,
            },
            isSaving: {
                type: Boolean,
            },
            isLoading: {
                type: Boolean,
            },
            authorizations: {
                type: Array,
            },
            sport: {
                type: String,
            },
            parentSite: {
                type: String,
            },
            defaultParentSite: {
                type: String,
            },
            selectedTab: {
                type: String,
            },
        };
    }

    updated(changedProps) {
        if (changedProps.has('siteData')) {
            const { selectedTab } = this;
            // since not every site can change season data we need to reset the tab if we move from one that can to one that cannot
            if (selectedTab === TABS.LEAGUES) {
                const canChangeLeagueData = this.canShowLeagueData();
                if (!canChangeLeagueData) {
                    this.selectedTab = TABS.PROD;
                }
            }
            if (selectedTab === TABS.STAGING) {
                if (this.isViewMode()) {
                    this.selectedTab = TABS.PROD;
                }
            }
        }
        if (changedProps.has('siteData')) {
            const { selectedTab } = this;
            if (selectedTab === TABS.TOKENS) {
                if (!this.canShowTokens()) {
                    this.selectedTab = TABS.PROD;
                }
            }
            if (selectedTab === TABS.HISTORY) {
                if (!this.canShowHistory()) {
                    this.selectedTab = TABS.PROD;
                }
            }
        }
        if (changedProps.has('detailMode')) {
            const { selectedTab } = this;
            // when we exit edit mode on the staging tab we need to reset the selected tab
            if (selectedTab === TABS.STAGING) {
                if (this.isViewMode()) {
                    this.selectedTab = TABS.PROD;
                }
            }
        }
    }

    render() {
        return html`<div class="fixed-header">
                ${this.renderHeader()} ${this.renderControls()} ${this.renderValidationErrors()}
            </div>
            ${this.renderMain()}`;
    }

    renderHeader() {
        const { siteData, sport, parentSite } = this;
        const { core = {} } = siteData;
        const { name } = core;
        if (this.isViewMode()) {
            return html`<h1>${name}</h1>`;
        }
        if (this.isEditMode()) {
            return html`<h1>Edit: ${name}</h1>`;
        }
        if (this.isAddNewMode()) {
            return html`<h1>New: ${sport} ${parentSite ? `${parentSite} child ` : null} site</h1>`;
        }
    }

    renderStatusMessages() {
        const { isLoading, isSaving } = this;
        if (isLoading) {
            //return html`<div>Loading...</div>`;
            return html`<loading-logo></loading-logo>`;
        }
        if (isSaving) {
            return html`<div>Saving...</div>`;
        }
    }

    handleSportChanged({ detail }) {
        const { value } = detail;
        this.sport = value;
        // todo action to change sport so we can use different template etc
    }

    renderMain() {
        // todo move sport to redux, use selector to get template
        const { siteTemplate, sport, isLoading, isSaving } = this;
        if (isLoading || isSaving) {
            return this.renderStatusMessages();
        }
        if (!siteTemplate) {
            return html`<div>No template found for ${sport}</div>`;
        }

        return this.renderForm();
    }

    isViewMode() {
        const { detailMode } = this;
        return detailMode === DETAIL_MODES.VIEW;
    }

    isAddNewMode() {
        const { detailMode } = this;
        return detailMode === DETAIL_MODES.NEW;
    }

    isEditMode() {
        const { detailMode } = this;
        return detailMode === DETAIL_MODES.EDIT;
    }

    renderValidationErrors() {
        const { validationErrors } = this;
        if (validationErrors && Object.keys(validationErrors).length) {
            // flatten tweak display
            const keySeparator = ' -> ';
            const flattenedValidationObj = flattenObject(validationErrors, keySeparator);
            const flattenedKVPs = Object.entries(flattenedValidationObj);
            // if we don't have any values then we don't need to render this
            if (flattenedKVPs.some(([_, value]) => value)) {
                return html` <div class="errors">
                    <h2>Validation Errors</h2>
                    <ul>
                        ${flattenedKVPs.map(([key, msg]) => {
                            //const anchor = key.replaceAll(keySeparator, '.');
                            // not quite working about of the box
                            //<a @click=${() => false} href="#${anchor}">${key}</a>
                            return html`<li><b>${key}</b>: ${msg}</li>`;
                        })}
                    </ul>
                </div>`;
            }
        }
    }

    renderControls() {
        const { siteData, sport, isSaving, isLoading, authorizations, parentSite } = this;
        const disableForAction = isSaving || isLoading;
        const { meta = {} } = siteData || {};
        // todo again, probably should be a selector..
        const showAddChildSite =
            meta?.canAcceptChildSites && authorizations.includes(CAN_EDIT_SITES);
        const canEditRoot = authorizations.includes(CAN_EDIT_ROOT_SITES);
        const showAddSite = canEditRoot || (parentSite && authorizations.includes(CAN_EDIT_SITES));
        // for now, copy isn't relevant for non root since we can't carry over anything (we only allow changing the names)
        // todo revisit this in soccer/cricket
        const showCopy = showAddSite && canEditRoot;
        const showDpClient = canEditRoot;
        const showActivation = showCopy; // for now these are the same
        // todo don't show new/copy if you can't add do so (i.e. non-root ability on root site)
        const controls = [];
        if (this.isEditMode() || this.isAddNewMode()) {
            const saveText = this.isAddNewMode() ? 'Create New' : 'Save';
            controls.push(
                html`<tmn-button
                    .disabled=${disableForAction}
                    color="primary"
                    @click=${this.handleSave}
                    >${saveText}</tmn-button
                >`
            );
        }
        if (this.isViewMode()) {
            controls.push(
                html`<tmn-button
                    .disabled=${disableForAction}
                    color="primary"
                    @click=${this.editSite}
                    >Edit
                </tmn-button>`
            );
        }
        if (this.isEditMode()) {
            controls.push(
                html`<tmn-button
                    .disabled=${disableForAction}
                    color="primary"
                    @click=${this.viewSite}
                    >Cancel</tmn-button
                >`
            );
        }
        if (showCopy && (this.isViewMode() || this.isEditMode())) {
            controls.push(
                html`<tmn-button .disabled=${disableForAction} @click=${this.handleCopySite}>
                    Copy
                </tmn-button>`
            );
        }
        if (!this.isAddNewMode()) {
            if (showActivation) {
                if (siteData.authorizations?.softDeactivation) {
                    controls.push(
                        html`<tmn-button
                            .disabled=${disableForAction}
                            @click=${this.handleSoftActivate}
                            >Remove Soft Deactivation
                            (${siteData.authorizations?.softDeactivation})</tmn-button
                        >`
                    );
                } else {
                    controls.push(
                        html`
                            <soft-deactivate-btn
                                @soft-deactivate-confirmed=${this.handleSoftDeactivate}>
                            </soft-deactivate-btn>
                        `
                    );
                }
                if (siteData.active) {
                    controls.push(
                        html`<tmn-button
                            .disabled=${disableForAction}
                            @click=${this.handleDeactivate}
                            >Deactivate</tmn-button
                        >`
                    );
                } else {
                    controls.push(
                        html`<tmn-button .disabled=${disableForAction} @click=${this.handleActivate}
                            >Activate</tmn-button
                        >`
                    );
                }
            }
            if (showAddSite) {
                controls.push(html`<tmn-button .disabled=${disableForAction} @click=${this.newSite}>
                    New Site
                </tmn-button>`);
            }
            if (showAddChildSite) {
                controls.push(html`<tmn-button
                    .disabled=${disableForAction}
                    @click=${this.newChildSite}>
                    New Child Site
                </tmn-button>`);
            }
            if (showDpClient) {
                controls.push(html`<tmn-button
                    .disabled=${disableForAction}
                    @click=${this.launchDPQueryTool}>
                    DP Query Tool
                </tmn-button>`);
            }
        }
        return html`<div class="controls-container">${controls}</div> `;
    }

    canShowLeagueData() {
        const { siteTemplate, authorizations, siteData, parentSite } = this;
        const { meta = {} } = siteData || {};
        const { leagueData } = siteTemplate;
        const canEditSites = authorizations.includes(CAN_EDIT_SITES);
        return (
            leagueData &&
            (meta.canEditLeagueData || (parentSite && canEditSites && this.isAddNewMode()))
        );
    }

    canShowTokens() {
        const { authorizations } = this;
        // todo eventually this should probably also have an option for CAN_MANAGE_OWN_SITE_TOKENS
        // whereby we can let customers manage some degree of their own tokens.
        // In that case it should follow the site meta.canEditLeagueData pattern that canShowLeagueData follows above
        const canManageTokens =
            authorizations.includes(CAN_MANAGE_MASTER_TOKENS) && !this.isAddNewMode();
        return canManageTokens;
    }

    canShowHistory() {
        const { authorizations = [] } = this;
        return authorizations.includes(CAN_SEE_SITE_HISTORY) && !this.isAddNewMode();
    }

    renderForm() {
        const { siteTemplate, authorizations, siteData, selectedTab = TABS.PROD } = this;
        const isViewMode = this.isViewMode();
        if (isViewMode && !siteData?.core?.name) {
            return null;
        }
        const stagingOverridesPath = STAGING_OVERRIDE_PATH.split('.');
        const canEditRoot = authorizations.includes(CAN_EDIT_ROOT_SITES);
        const { leagueData, ...restSiteTemplate } = siteTemplate;
        // should we make this can canEditLeagueData if edit OR hasParent and <something> if new?
        const showLeaguData = this.canShowLeagueData();
        const showTokens = this.canShowTokens();
        const showHistory = this.canShowHistory();
        const showStaging = canEditRoot && !isViewMode;
        return html`
            <tmn-tab-group
                .selectedValue=${selectedTab}
                @tmn-selected-value-changed=${this.handleSelectedTab}>
                <tmn-tabs>
                    <tmn-tab .value=${TABS.PROD}>Production</tmn-tab>
                    ${showLeaguData
                        ? html`<tmn-tab .value=${TABS.LEAGUES}>Leagues</tmn-tab>`
                        : null}
                    ${showStaging
                        ? html`<tmn-tab .value=${TABS.STAGING}>Staging Overrides</tmn-tab>`
                        : null}
                    ${showTokens
                        ? html`<tmn-tab .value=${TABS.TOKENS}>Token Management</tmn-tab>`
                        : null}
                    ${showHistory ? html`<tmn-tab .value=${TABS.HISTORY}>History</tmn-tab>` : null}
                </tmn-tabs>
                <tmn-tab-panel .value=${TABS.PROD}
                    >${this.renderGroup(restSiteTemplate)}</tmn-tab-panel
                >
                ${showLeaguData
                    ? html`<tmn-tab-panel .value=${TABS.LEAGUES}
                          >${this.renderLeagueData()}</tmn-tab-panel
                      >`
                    : null}
                ${showStaging
                    ? html`<tmn-tab-panel .value=${TABS.STAGING}
                          >${this.renderGroup(restSiteTemplate, null, stagingOverridesPath, {
                              isOverrideMode: true,
                          })}</tmn-tab-panel
                      >`
                    : null}
                ${showTokens
                    ? html`<tmn-tab-panel .value=${TABS.TOKENS}
                          >${this.renderTokenManagement()}</tmn-tab-panel
                      >`
                    : null}
                ${showHistory
                    ? html`<tmn-tab-panel .value=${TABS.HISTORY}
                          >${this.renderHistory()}</tmn-tab-panel
                      >`
                    : null}
            </tmn-tab-group>
        `;
    }

    handleSelectedTab(e) {
        const { detail } = e;
        if (this.isViewMode() && detail.value === TABS.STAGING) {
            // this is a hack, not quite sure why this events lands after the update to selected tab..
            return;
        }
        this.selectedTab = detail.value;
    }

    renderTokenManagement() {
        return html`<site-tokens></site-tokens>`;
    }

    renderHistory() {
        return html`<site-history></site-history>`;
    }

    // renderViewDetails() {
    //     const { siteTemplate, sport, siteData } = this;
    //     if (!siteData?.core?.name) {
    //         return null;
    //     }
    //     const { meta = {} } = siteData || {};
    //     const { leagueData, ...restSiteTemplate } = siteTemplate;
    //     const canChangeLeagueData = leagueData && meta.canEditLeagueData;

    //     // may have to show tabs in view mode as well and slightly rework
    //     return html`
    //         <tmn-tab-group>
    //             <tmn-tabs>
    //                 <tmn-tab>Production</tmn-tab>
    //                 ${canChangeLeagueData ? html`<tmn-tab>LEAGUES</tmn-tab>` : null}
    //             </tmn-tabs>
    //             <tmn-tab-panel>${this.renderGroup(restSiteTemplate)}</tmn-tab-panel>
    //             ${canChangeLeagueData
    //                 ? html`<tmn-tab-panel>${this.renderLeagueData()}</tmn-tab-panel>`
    //                 : null}
    //         </tmn-tab-group>
    //     `;
    // }

    renderGroup(
        group,
        groupLabel,
        parentPath = [],
        { rowDisplayFormat = false, isOverrideMode = false, sectionDisplayFormat = false } = {}
    ) {
        let direction = rowDisplayFormat ? 'row' : 'column';
        const size = rowDisplayFormat ? '4' : '';
        const classes = classMap({ group: true });
        return html`${this.renderGroupHeader(groupLabel, parentPath.length, isOverrideMode)}
            <tmn-grid class=${classes} container .direction=${direction}>
                ${Object.entries(group).map(([key, item]) => {
                    const secondaryClasses = classMap({
                        section: item?.sectionDisplayFormat,
                    });
                    direction = item?.rowDisplayFormat ? 'row' : 'column';
                    if (item.hidden) {
                        return null;
                    }
                    return html`<tmn-grid
                        class=${secondaryClasses}
                        container
                        .direction=${direction}
                        item
                        .md=${size}>
                        ${this.renderTemplateItem([key, item], parentPath, {
                            isOverrideMode,
                        })}</tmn-grid
                    >`;
                })}
            </tmn-grid> `;
    }

    renderTemplateItem([key, item], parentPath = [], { isOverrideMode = false } = {}) {
        if (isOverrideMode && item.notOverridable) {
            return null;
        }
        if (typeof item === 'object' && item?.type === 'GROUP' && item?.properties) {
            parentPath = [...parentPath.slice(0), key];
            const groupLabel = item?.displayKey || key;
            return this.renderGroup(item.properties, groupLabel, parentPath, {
                rowDisplayFormat: item.rowDisplayFormat,
                sectionDisplayFormat: item.sectionDisplayFormat,
                isOverrideMode: isOverrideMode,
            });
        }
        const path = [...parentPath.slice(0), key].join('.');
        return html`
            <form-element-site
                .itemTemplate=${item}
                .path=${path}
                .isOverrideMode=${isOverrideMode}></form-element-site>
        `;
    }

    renderGroupHeader(label, depth = 0, isOverrideMode) {
        // console.log(label, depth, isOverrideMode);
        // todo fixup depth for staging
        switch (depth) {
            case 0: {
                return isOverrideMode ? html`<h2>Staging and Development Overrides</h2>` : null;
                //return html`<h2>${mainTitle}</h2>`;
            }
            case 1:
                return html`<h2>${label}</h2>`;
            case 2:
                return html`<h3>${label}</h3>`;
            case 3:
                return html`<h4>${label}</h4>`;
            default:
                return html`<h4>${label}</h4>`;
        }
    }

    renderLeagueData() {
        //return html`<div>SEASON DATA HERE</div>`;
        const { siteTemplate } = this;
        const path = 'leagueData';
        const item = siteTemplate[path];
        if (!item) {
            return null;
        }
        return html`
            <form-element-site
                class="site-season"
                .itemTemplate=${item}
                .path=${path}></form-element-site>
        `;
    }

    editSite() {
        const { siteData } = this;
        this.actions.editSiteDetails({ name: siteData.core.name });
    }

    viewSite() {
        const { siteData } = this;
        this.actions.viewSiteDetails({ name: siteData.core.name });
    }

    newSite() {
        const { sport, parentSite, authorizations, defaultParentSite } = this;
        if (!authorizations.includes(CAN_EDIT_SITES)) {
            return;
        }
        if (!authorizations.includes(CAN_EDIT_ROOT_SITES)) {
            const parentForNewSite = parentSite || defaultParentSite;
            if (parentForNewSite) {
                routeActions.newSiteRoute(sport, parentForNewSite);
            }
            return;
        }
        routeActions.newSiteRoute(sport);
    }

    newChildSite() {
        // dirty check in here?
        const { sport, siteData } = this;
        const siteName = siteData?.core?.name;
        routeActions.newSiteRoute(sport, siteName);
        // this.actions.setParentSite({ parentSite: siteName, sport });
        // this.actions.newSite();
    }

    launchDPQueryTool(e) {
        e.stopPropagation();
        const { siteData } = this;
        if (siteData?.dpConfig?.credentials?.key) {
            const { dpConfig } = siteData;
            const { credentials, baseURLTest } = dpConfig;
            const { client, key } = credentials;
            const url = new URL(baseURLTest);
            url.username = client;
            url.password = key;
            url.pathname = '/execute.htm';
            window.open(url.href, '_blank');
        }
    }

    handleCopySite() {
        const { siteData } = this;
        this.actions.copySite({ siteData });
    }

    handleSave() {
        const { siteData } = this;
        const data = { ...siteData, sport: this.sport };
        if (this.isEditMode()) {
            this.actions.saveSite({ data });
            return;
        }
        this.actions.addSite({ data });
    }

    handleActivate() {
        const { siteData } = this;
        this.actions.activateSite({ guid: siteData.guid });
    }

    handleDeactivate() {
        const { siteData } = this;
        this.actions.deactivateSite({ guid: siteData.guid });
    }

    handleSoftDeactivate(e) {
        const { siteData } = this;
        const { softDeactivation } = e.detail;
        this.actions.softDeactivateSite({ guid: siteData.guid, softDeactivation });
    }

    handleSoftActivate() {
        const { siteData } = this;
        this.actions.softActivateSite({ guid: siteData.guid });
    }
}
// maybe dirty flags by key
// we can just deep get validation errors if desired on render
const mapStateToProps = (state, ownProps) => {
    return (state) => {
        return {
            siteTemplate: selectors.siteConfigTemplateSelector(state),
            validationErrors: selectors.siteValidationErrorsSelector(state),
            siteData: selectors.siteStagedEditsSelector(state),
            detailMode: selectors.siteDetailModeSelector(state),
            isSaving: selectors.siteIsSavingSelector(state),
            isLoading: selectors.siteIsLoadingSelector(state),
            authorizations: accountSelectors.accountAuthorizationsSelector(state),
            sport: selectors.siteSportSelector(state),
            parentSite: selectors.selectedParentSiteSelector(state),
            defaultParentSite: selectors.defaultParentSiteSelector(state),
        };
    };
};

const combinedActions = {
    ...actions,
    //...appActions,
};

customElements.define('site-detail', connect(mapStateToProps, combinedActions)(SiteDetail));

/*
            <div>Staged Edits:</div>
            <pre>
                ${JSON.stringify(this.stagedEdits, null, 4)}
            </pre>

            <div>SiteData:</div>
            <pre>
                ${JSON.stringify(this.siteData, null, 4)}
            </pre>
    */
